import React, { useEffect, useState } from 'react';
import { Layout, Menu, Dropdown, Button, Avatar, Modal, Badge, Divider } from 'antd';
import { 
  DashboardOutlined, 
  MedicineBoxOutlined, 
  TeamOutlined, 
  UserOutlined, 
  PlusCircleOutlined, 
  BellOutlined, 
  SettingOutlined,
  LogoutOutlined,
  UserAddOutlined,
  CalendarOutlined,
  FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import PostJobScreen from './PostJobScreen';
import Dashboard from './Dashboard';
import ProfileScreen from './ProfileScreen';
import JobManagement from './JobManagement';
import JobDetails from './JobDetails';
import CandidateDetail from './CandidateDetails';
import OngoingEngagements from './OngoingEngagements';
import CandidateAssignments from './CandidateAssignments';
import Setting from './Organization';
import { useAuth } from '../../AuthProvider';
import { getOrg } from '../../api';
import logo from '../../assets/images/bg/logo.svg';
import logo512 from '../../assets/images/bg/logo512.png';

const { Header, Sider, Content } = Layout;

const DashboardScreen = () => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [employerDetails, setEmployerDetails] = useState(null);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const getEmployerDetails = async () => {
      try {
        const response = await getOrg();
        setEmployerDetails(response.data);
      } catch (error) {
        console.error('Error fetching employer details:', error);
        setIsDetailModalVisible(true);
      }
    };

    getEmployerDetails();
  }, []);

  const handleLogout = () => {
    Modal.confirm({
      title: 'Confirm Logout',
      content: 'Are you sure you want to logout?',
      okText: 'Yes, Logout',
      cancelText: 'Cancel',
      onOk: logout,
    });
  };

  const menu = (
    <Menu style={{ minWidth: '200px', borderRadius: '8px' }}>
      <Menu.Item key="profile" icon={<UserOutlined />}>
        <Link to="/employer/profile">My Profile</Link>
      </Menu.Item>
      <Menu.Item key="settings" icon={<SettingOutlined />}>
        <Link to="/employer/setting">Settings</Link>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
        Logout
      </Menu.Item>
    </Menu>
  );

  const selectedKey = () => {
    if (location.pathname.includes('/employer/post-job')) return 'create-assignment';
    if (location.pathname.includes('/employer/job-management')) return 'manage-assignments';
    if (location.pathname.includes('/employer/job-details') || location.pathname.includes('/employer/candidate-detail/')) return 'manage-assignments';
    if (location.pathname.includes('/employer/setting')) return 'organization-settings';
    if (location.pathname.includes('/employer/profile')) return 'profile';
    if (location.pathname.includes('/employer/assignments')) return 'candidate-applications';
    if (location.pathname.includes('/employer/ongoing-management')) return 'active-engagements';
    return 'dashboard';
  };

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/employer">Dashboard</Link>,
    },
    {
      key: 'create-assignment',
      icon: <PlusCircleOutlined />,
      label: <Link to="/employer/post-job">Post Job</Link>,
    },
    {
      key: 'manage-assignments',
      icon: <FileTextOutlined />,
      label: <Link to="/employer/job-management">Job Management</Link>,
    },
    {
      key: 'candidate-applications',
      icon: <UserAddOutlined />,
      label: <Link to="/employer/assignments">Applications</Link>,
    },
    {
      key: 'active-engagements',
      icon: <CalendarOutlined />,
      label: <Link to="/employer/ongoing-management">Ongoing</Link>,
    },
    {
      type: 'divider',
    },
    {
      key: 'organization-settings',
      icon: <MedicineBoxOutlined />,
      label: <Link to="/employer/setting">Facility Settings</Link>,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <Sider 
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        style={{ 
          background: '#ffffff',
          boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
          borderRight: '1px solid #f0f0f0'
        }} 
        width={260}
        collapsedWidth={80}
        trigger={null}
      >
        {/* Logo */}
        <div style={{ 
          height: '64px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: collapsed ? '0' : '0 24px',
          borderBottom: '1px solid #f0f0f0',
          marginBottom: '8px'
        }}>
          {collapsed ? (
            <img src={logo512} style={{ height: '40px', width: '40px' }} alt="VitalStaf" />
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <img src={logo} style={{ height: '32px', marginRight: '12px' }} alt="VitalStaf" />
              </div>
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{
                  fontSize: '16px',
                  width: 32,
                  height: 32,
                  color: '#595959',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </>
          )}
        </div>

        {/* Navigation Menu */}
        <Menu 
          theme="light" 
          selectedKeys={[selectedKey()]} 
          mode="inline" 
          items={menuItems}
          style={{ 
            borderRight: 0,
            background: 'transparent',
            fontSize: '14px'
          }}
        />

        {/* Organization Info (when not collapsed) */}
        {!collapsed && employerDetails && (
          <div style={{ 
            position: 'absolute', 
            bottom: '24px', 
            left: '24px', 
            right: '24px',
            padding: '16px',
            background: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #f0f0f0'
          }}>
            <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>
              Organization
            </div>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '500', 
              color: '#262626',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {employerDetails.name || 'Healthcare Facility'}
            </div>
          </div>
        )}
      </Sider>

      <Layout>
        {/* Header */}
        <Header style={{
          padding: '0 24px',
          background: '#ffffff',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '64px'
        }}>
          <div></div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Notifications */}
            <Badge count={0} size="small">
              <Button 
                type="text" 
                icon={<BellOutlined />} 
                style={{ 
                  color: '#595959',
                  border: 'none',
                  background: 'transparent'
                }}
              />
            </Badge>

            {/* User Dropdown */}
            <Dropdown 
              overlay={menu} 
              trigger={['click']} 
              placement="bottomRight"
            >
              <Button 
                type="text" 
                style={{ 
                  padding: '4px 12px',
                  height: 'auto',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Avatar 
                  style={{ 
                    backgroundColor: '#1890ff',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                  size="small"
                >
                  {currentUser?.displayName?.[0] || currentUser?.email?.[0] || 'U'}
                </Avatar>
                <span style={{ 
                  color: '#262626', 
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User'}
                </span>
              </Button>
            </Dropdown>
          </div>
        </Header>

        {/* Content */}
        <Content style={{ 
          margin: '24px',
          background: '#f5f5f5',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{ 
            background: 'transparent',
            minHeight: '100%',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <Routes>
              <Route path="post-job" element={<PostJobScreen />} />
              <Route path="job-management" element={<JobManagement />} />
              <Route path="job-details" element={<JobDetails />} />
              <Route path="candidate-detail/:candidateId" element={<CandidateDetail />} />
              <Route path="setting" element={<Setting organizationData={employerDetails} />} />
              <Route path="assignments" element={<CandidateAssignments organizationData={employerDetails} />} />
              <Route path="ongoing-management" element={<OngoingEngagements organizationData={employerDetails} />} />
              <Route path="profile" element={<ProfileScreen />} />
              <Route path="/" element={<Dashboard />} />
            </Routes>
          </div>
        </Content>
      </Layout>

      {/* Organization Setup Modal */}
      <Modal
        title="Complete Organization Setup"
        open={isDetailModalVisible}
        onOk={() => {
          setIsDetailModalVisible(false);
          navigate('/employer/setting');
        }}
        okText="Complete Setup"
        closable={false}
        maskClosable={false}
        cancelButtonProps={{ style: { display: 'none' } }}
        centered
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: '16px', color: '#595959' }}>
            To get started with VitalStaf, please complete your healthcare facility information.
          </p>
          <p style={{ margin: 0, fontSize: '14px', color: '#8c8c8c' }}>
            This information helps us provide better staffing solutions for your facility.
          </p>
        </div>
      </Modal>
    </Layout>
  );
};

export default DashboardScreen;
